import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GoogleIcon from '@mui/icons-material/Google';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RouteIcon from '@mui/icons-material/Route';
import { useAuth } from '../contexts/AuthContext';
import CredentialLogos from './CredentialLogos';

const benefits = [
  ['Pattern courses', 'Work through the templates before drilling problems.'],
  ['Internal practice', 'Solve TopSWE100 and course problems without leaving the workspace.'],
  ['Retention tools', 'Use quizzes, flashcards, and roadmap reviews after each chapter.'],
];

const previewRows = [
  ['Courses', 'Pattern templates'],
  ['Practice', 'Internal judge'],
  ['Review', 'Quizzes + cards'],
];

function SignIn() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const { error: signInError } = await signInWithGoogle();
      if (signInError) throw signInError;
    } catch (signInError) {
      setError(signInError.message);
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', color: 'text.primary', minHeight: 'calc(100vh - 72px)' }}>
      <Box
        component="section"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: (theme) => theme.palette.custom.heroGradient,
          pt: { xs: 6, md: 10 },
          pb: { xs: 6, md: 9 },
          '&::after': {
            content: '""',
            position: 'absolute',
            right: { xs: '-54%', md: '-18%' },
            top: { xs: '8%', md: '-12%' },
            width: { xs: 520, md: 820 },
            height: { xs: 520, md: 820 },
            background: 'linear-gradient(135deg, rgba(97, 111, 255, 0.38), rgba(115, 213, 255, 0.18), rgba(255,255,255,0))',
            filter: 'blur(24px)',
            transform: 'rotate(-18deg)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 4, md: 7 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
                <Stack spacing={3.2} alignItems={{ xs: 'center', md: 'flex-start' }} textAlign={{ xs: 'center', md: 'left' }}>
                  <Stack direction="row" spacing={1.4} alignItems="center">
                    <Box component="img" src="/topswe-logo.png" alt="TopSWE" sx={{ width: 44, height: 44, borderRadius: 2.5 }} />
                    <Typography sx={{ fontSize: '1.2rem', fontWeight: 850 }}>TopSWE</Typography>
                  </Stack>

                  <Chip
                    icon={<LockOutlinedIcon sx={{ fontSize: 17 }} />}
                    label="Secure account access"
                    sx={{
                      width: 'fit-content',
                      bgcolor: 'background.paper',
                      color: 'text.primary',
                      border: '1px solid',
                      borderColor: 'divider',
                      fontWeight: 700,
                      boxShadow: '0 14px 40px rgba(82, 107, 170, 0.08)',
                    }}
                  />

                  <Box>
                    <Typography
                      variant="h1"
                      sx={{
                        maxWidth: 700,
                        fontSize: { xs: '3rem', sm: '4rem', md: '5rem' },
                        lineHeight: 0.96,
                        fontWeight: 850,
                        letterSpacing: 0,
                        mb: 2.5,
                      }}
                    >
                      Continue your interview prep.
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        maxWidth: 600,
                        color: 'text.secondary',
                        lineHeight: 1.45,
                        fontWeight: 400,
                      }}
                    >
                      Sign in to keep your courses, practice problems, quizzes, flashcards, and roadmap in one focused workspace.
                    </Typography>
                  </Box>

                  <Stack spacing={1.4} sx={{ width: '100%', maxWidth: 560 }}>
                    {benefits.map(([title, text]) => (
                      <Stack key={title} direction="row" spacing={1.4} alignItems="flex-start">
                        <CheckCircleOutlineIcon sx={{ color: 'primary.main', mt: 0.2 }} />
                        <Box>
                          <Typography sx={{ fontWeight: 800 }}>{title}</Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                            {text}
                          </Typography>
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.08 }}>
                <Paper
                  elevation={0}
                  sx={{
                    maxWidth: 520,
                    mx: { xs: 'auto', md: 0 },
                    ml: { md: 'auto' },
                    p: { xs: 2.5, sm: 3.5, md: 4 },
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'rgba(255,255,255,0.86)',
                    backdropFilter: 'blur(18px)',
                    boxShadow: '0 28px 90px rgba(82, 107, 170, 0.16)',
                  }}
                >
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800 }}>
                        Account
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 850, lineHeight: 1.05, mt: 0.5 }}>
                        Sign in with Google
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', mt: 1.2, lineHeight: 1.65 }}>
                        OAuth keeps access simple and avoids another password to manage.
                      </Typography>
                    </Box>

                    {error && (
                      <Alert severity="error" sx={{ border: '1px solid', borderColor: 'error.light' }}>
                        {error}
                      </Alert>
                    )}

                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleGoogleSignIn}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />}
                      endIcon={!loading ? <ArrowForwardIcon /> : null}
                      sx={{
                        justifyContent: 'center',
                        py: 1.45,
                        px: 3,
                        bgcolor: '#0a0d14',
                        color: '#fff',
                        boxShadow: '0 18px 46px rgba(10, 13, 20, 0.18)',
                        '&:hover': {
                          bgcolor: '#172033',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 22px 56px rgba(10, 13, 20, 0.22)',
                        },
                        '&.Mui-disabled': {
                          bgcolor: 'rgba(10, 13, 20, 0.38)',
                          color: 'rgba(255, 255, 255, 0.74)',
                        },
                      }}
                    >
                      {loading ? 'Redirecting...' : 'Continue with Google'}
                    </Button>

                    <Divider />

                    <Stack spacing={1.2}>
                      {previewRows.map(([label, value]) => (
                        <Stack
                          key={label}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            py: 1.1,
                            borderBottom: '1px solid',
                            borderColor: label === 'Review' ? 'transparent' : 'divider',
                          }}
                        >
                          <Typography sx={{ color: 'text.secondary' }}>{label}</Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <RouteIcon sx={{ fontSize: 17, color: 'primary.main' }} />
                            <Typography sx={{ fontWeight: 800 }}>{value}</Typography>
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>

                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
                      By signing in, you agree to our{' '}
                      <Typography
                        component="button"
                        onClick={() => navigate('/legal')}
                        sx={{
                          p: 0,
                          border: 0,
                          bgcolor: 'transparent',
                          color: 'text.primary',
                          font: 'inherit',
                          fontWeight: 800,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        Terms
                      </Typography>
                      {' '}and{' '}
                      <Typography
                        component="button"
                        onClick={() => navigate('/privacy')}
                        sx={{
                          p: 0,
                          border: 0,
                          bgcolor: 'transparent',
                          color: 'text.primary',
                          font: 'inherit',
                          fontWeight: 800,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        Privacy Policy
                      </Typography>
                      .
                    </Typography>
                  </Stack>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 4, md: 5 }, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', textAlign: 'center', mb: 2.5 }}>
            Created by a team who worked at
          </Typography>
          <CredentialLogos compact />
        </Container>
      </Box>
    </Box>
  );
}

export default SignIn;
